<template>
  <div class="row mx-0">
    <div class="col-12 px-0">
      <div class="card border-0 rounded-0">
        <div class="card-body mini-nav bg-gradient pt-2 pb-2 px-0">
          <nav class="navbar navbar-expand-md navbar py-1 px-3">
            <button
                class="navbar-toggler btn btn-sm px-3 font-size-16 header-item waves-effect h-auto"
                type="button" data-toggle="collapse" data-target="#navbarNavAltMarkup"
                aria-controls="navbarNavAltMarkup" aria-expanded="false"
                aria-label="Toggle navigation">
              <span class="mdi mdi-menu"></span>
            </button>
            <div class="collapse navbar-collapse justify-content-between"
                 id="navbarNavAltMarkup">
              <div class="navbar-nav">
                <a :class="activeClass" @click="goTo('markets_dashboard')" href="javascript:void(0)">
                  <i class="bx bx-line-chart me-1"></i>Revenue Dashboard
                </a>

                <a :class="activeClass6" @click="goTo('markets_analytics')" href="javascript:void(0)">
                  <i class="bx bx-bar-chart-alt-2 me-1"></i>Analytics
                </a>

              </div>
            </div>
          </nav>
        </div>
      </div>
    </div>

    <!-- Scrolling Stats Banner -->
    <div class="col-12 px-0">
      <div class="stats-banner-markets" @mouseenter="pauseScrolling" @mouseleave="resumeScrolling">
        <div class="stats-gradient-overlay"></div>
        <transition name="fade">
          <div v-if="statsLoading" class="stats-loading-indicator">
            <i class="bx bx-loader-alt bx-spin"></i> Updating...
          </div>
        </transition>
        <div class="stats-scroll" :style="{ transform: `translateX(${scrollPosition}px)` }">
          <div class="stats-item" v-for="(stat, index) in duplicatedStats" :key="`stat-${index}`">
            <span class="stats-icon-wrapper" :class="getStatColorClass(stat.label)">
              <i :class="getStatIconClass(stat.label)"></i>
            </span>
            <div class="stats-content">
              <span class="stats-label">{{ stat.label }}</span>
              <span class="stats-value">{{ stat.value }}</span>
            </div>
            <span class="stats-divider" v-if="index < duplicatedStats.length - 1"></span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { execute, biller } from "@/api";

export default {
  name: "MarketsToolBar",
  props: {
    selectedStream: {
      type: Object,
      default: () => ({})
    },
    dateRange: {
      type: Object,
      default: () => ({ type: 'this_month', startDate: '', endDate: '' })
    }
  },
  data() {
    return {
      currentPage: '',
      scrollPosition: 0,
      scrollSpeed: 1,
      scrollInterval: null,
      isPaused: false,
      // Stats for the banner - will be updated from API based on date range
      marqueeStats: [
        { label: "PERIOD COLLECTION", value: "KES 0" },
        { label: "ACTIVE TRADERS", value: "0" },
        { label: "COMPLIANT TRADERS", value: "0" },
        { label: "DEFAULTERS", value: "0" },
        { label: "MARKET UTILIZATION", value: "0%" },
        { label: "TOP MARKET", value: "N/A" },
        { label: "TOP COLLECTOR", value: "N/A" },
        { label: "AVG TRANSACTION", value: "KES 0" },
        { label: "PEAK HOUR", value: "N/A" },
        { label: "ALERTS", value: "0" }
      ],
      statsLoading: false,
      lastFetchedParams: null
    }
  },

  computed: {
    duplicatedStats() {
      // Duplicate the array to create seamless scrolling
      return [...this.marqueeStats, ...this.marqueeStats];
    },
    activeClass() {
      return {
        'nav-item': true,
        'nav-link': true,
        'active': this.currentPage === 'markets_dashboard'
      }
    },
    activeClass6() {
      return {
        'nav-item': true,
        'nav-link': true,
        'active': this.currentPage === 'markets_analytics'
      }
    }
  },

  watch: {
    // Watch for changes in selected stream
    'selectedStream.incomeTypePrefix': {
      handler(newVal) {
        if (newVal) {
          console.log('MarketsToolBar: Stream changed to:', newVal);
          this.getMarketStats();
        }
      },
      immediate: true
    },
    // Watch for changes in date range
    dateRange: {
      handler(newVal, oldVal) {
        if (newVal && this.selectedStream.incomeTypePrefix) {
          // Create a unique key for the current parameters
          const currentParams = `${this.selectedStream.incomeTypePrefix}_${newVal.type}_${newVal.startDate}_${newVal.endDate}`;

          // Only fetch if parameters have actually changed
          if (currentParams !== this.lastFetchedParams) {
            console.log('MarketsToolBar: Date range changed:', {
              old: oldVal,
              new: newVal,
              stream: this.selectedStream.incomeTypePrefix
            });
            this.getMarketStats();
          }
        }
      },
      deep: true
    }
  },

  mounted() {
    this.currentPage = sessionStorage.getItem("currentPage");
    this.loadCachedStats();
    this.startScrolling();
    this.updateStatsData();

    // Initial load if stream is already selected
    if (this.selectedStream.incomeTypePrefix) {
      this.getMarketStats();
    }
  },

  beforeUnmount() {
    this.stopScrolling();
  },

  methods: {
    goTo(route) {
      sessionStorage.setItem("currentPage", route);
      this.$router.push(route);
    },

    loadCachedStats() {
      const cacheKey = `marketMarqueeStats_${this.selectedStream.incomeTypePrefix || 'MKT'}_${this.dateRange.type}`;
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (parsed.timestamp && Date.now() - parsed.timestamp < 300000) { // 5 minutes
            this.marqueeStats = parsed.data;
            this.lastFetchedParams = parsed.params;
          }
        } catch (e) {
          console.error('Error loading cached stats:', e);
        }
      }
    },

    async getMarketStats() {
      if (!this.selectedStream.incomeTypePrefix) {
        console.log('MarketsToolBar: No stream selected, skipping stats fetch');
        return;
      }

      // Create a unique key for the current parameters
      const currentParams = `${this.selectedStream.incomeTypePrefix}_${this.dateRange.type}_${this.dateRange.startDate}_${this.dateRange.endDate}`;

      // Prevent duplicate fetches
      if (currentParams === this.lastFetchedParams && !this.statsLoading) {
        console.log('MarketsToolBar: Already fetched for these parameters, skipping');
        return;
      }

      this.statsLoading = true;
      this.lastFetchedParams = currentParams;

      try {
        const data = new FormData();
        data.append("function", "getMarketsMarqueeStats");
        data.append("incomeTypePrefix", this.selectedStream.incomeTypePrefix);

        // Add date range parameters
        data.append("dateRange", this.dateRange.type);
        if (this.dateRange.type === 'custom') {
          data.append("startDate", this.dateRange.startDate);
          data.append("endDate", this.dateRange.endDate);
        }

        console.log('MarketsToolBar: Fetching marquee stats with params:', {
          stream: this.selectedStream.incomeTypePrefix,
          dateRange: this.dateRange.type,
          startDate: this.dateRange.startDate,
          endDate: this.dateRange.endDate
        });

        const response = await execute(data, biller);

        if (response.data.success) {
          this.marqueeStats = response.data.data.marqueeStats;

          // Cache the stats with date range key
          const cacheKey = `marketMarqueeStats_${this.selectedStream.incomeTypePrefix}_${this.dateRange.type}`;
          sessionStorage.setItem(cacheKey, JSON.stringify({
            timestamp: Date.now(),
            data: this.marqueeStats,
            params: currentParams,
            dateRange: this.dateRange
          }));

          console.log('MarketsToolBar: Updated marquee stats for', this.dateRange.type, ':', this.marqueeStats.length, 'items');
        } else {
          console.error('MarketsToolBar: Failed to fetch marquee stats:', response.data);
          // Don't reset lastFetchedParams on API failure to prevent infinite retries
        }
      } catch (error) {
        console.error("MarketsToolBar: Error fetching market stats:", error);
        // Don't reset lastFetchedParams on network error to prevent infinite retries
      } finally {
        this.statsLoading = false;
      }
    },

    // Get icon class based on stat label
    getStatIconClass(label) {
      const iconMap = {
        "TODAY'S COLLECTION": "bx bx-money",
        "YESTERDAY'S COLLECTION": "bx bx-money",
        "THIS WEEK'S COLLECTION": "bx bx-money",
        "THIS MONTH'S COLLECTION": "bx bx-money",
        "PERIOD COLLECTION": "bx bx-money",
        "ACTIVE TRADERS": "bx bx-group",
        "ACTIVE PARKERS": "bx bx-group",
        "ACTIVE BUSINESSES": "bx bx-group",
        "ACTIVE COLLECTORS": "bx bx-group",
        "ACTIVE TENANTS": "bx bx-group",
        "ACTIVE ADVERTISERS": "bx bx-group",
        "ACTIVE USERS": "bx bx-group",
        "COMPLIANT TRADERS": "bx bx-check-circle",
        "COMPLIANT PAYERS": "bx bx-check-circle",
        "COMPLIANT BUSINESSES": "bx bx-check-circle",
        "COMPLIANT TENANTS": "bx bx-check-circle",
        "COMPLIANT ADVERTISERS": "bx bx-check-circle",
        "DEFAULTERS": "bx bx-error-circle",
        "MARKET UTILIZATION": "bx bx-pie-chart-alt-2",
        "PARKING UTILIZATION": "bx bx-pie-chart-alt-2",
        "BUSINESS UTILIZATION": "bx bx-pie-chart-alt-2",
        "COLLECTION UTILIZATION": "bx bx-pie-chart-alt-2",
        "RENTAL UTILIZATION": "bx bx-pie-chart-alt-2",
        "ADVERTISEMENT UTILIZATION": "bx bx-pie-chart-alt-2",
        "UTILIZATION": "bx bx-pie-chart-alt-2",
        "TOP MARKET": "bx bx-trophy",
        "TOP PARKING ZONE": "bx bx-trophy",
        "TOP BUSINESS AREA": "bx bx-trophy",
        "TOP COLLECTION POINT": "bx bx-trophy",
        "TOP RENTAL AREA": "bx bx-trophy",
        "TOP AD ZONE": "bx bx-trophy",
        "TOP ZONE": "bx bx-trophy",
        "TOP COLLECTOR": "bx bx-medal",
        "AVG TRANSACTION": "bx bx-calculator",
        "PEAK HOUR": "bx bx-time",
        "ALERTS": "bx bx-bell"
      };
      return iconMap[label] || "bx bx-info-circle";
    },

    // Get color class based on stat label
    getStatColorClass(label) {
      if (label.includes("DEFAULTERS")) return "warning-stat";
      if (label.includes("COMPLIANT")) return "success-stat";
      if (label.includes("TOP")) return "primary-stat";
      if (label.includes("COLLECTION")) return "money-stat";
      return "";
    },

    // Scrolling animation methods
    startScrolling() {
      this.scrollInterval = setInterval(() => {
        if (!this.isPaused) {
          this.scrollPosition -= this.scrollSpeed;

          // Calculate total width of all stats
          const statsWidth = this.getContentWidth();

          // Reset position when first set has completely scrolled out
          if (Math.abs(this.scrollPosition) >= statsWidth) {
            this.scrollPosition = 0;
          }
        }
      }, 16); // 60fps
    },

    pauseScrolling() {
      this.isPaused = true;
    },

    resumeScrolling() {
      this.isPaused = false;
    },

    stopScrolling() {
      if (this.scrollInterval) {
        clearInterval(this.scrollInterval);
      }
    },

    getContentWidth() {
      // Approximate width per stat item
      const avgWidthPerItem = 280;
      return this.marqueeStats.length * avgWidthPerItem;
    },

    updateStatsData() {
      // Update stats periodically (every 2 minutes for non-today ranges, every 30 seconds for today)
      const updateInterval = this.dateRange.type === 'today' ? 30000 : 120000;

      setInterval(() => {
        if (this.selectedStream.incomeTypePrefix) {
          this.getMarketStats();
        }
      }, updateInterval);
    },

    numberFormat(num) {
      const britishNumberFormatter = new Intl.NumberFormat("en-GB");
      return britishNumberFormatter.format(num);
    }
  }
}
</script>

<style scoped>
/* Navigation bar gradient background */
.bg-gradient {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

/* Stats Banner Styles - Professional Theme */
.stats-banner-markets {
  width: 100%;
  height: 52px;
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  color: white;
  overflow: hidden;
  position: relative;
  margin: 0;
  border: none;
  padding: 0;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  cursor: pointer;
}

/* Gradient overlay for depth */
.stats-gradient-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg,
  rgba(30,41,59,1) 0%,
  rgba(30,41,59,0) 8%,
  rgba(30,41,59,0) 92%,
  rgba(30,41,59,1) 100%
  );
  z-index: 2;
  pointer-events: none;
}

/* Loading indicator */
.stats-loading-indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 3;
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
}

/* Remove all margins and padding from row and columns */
.row {
  margin: 0 !important;
}

.col-12 {
  padding: 0 !important;
}

.card {
  margin-bottom: 0 !important;
  border: none !important;
  border-radius: 0 !important;
}

.card-body {
  margin-bottom: 0 !important;
}

/* Remove border radius from navbar */
.navbar {
  border-radius: 0 !important;
}

/* Ensure the navbar background extends edge to edge */
.mini-nav {
  border-radius: 0 !important;
}

.stats-scroll {
  display: flex;
  align-items: center;
  height: 100%;
  white-space: nowrap;
  transition: transform 0.016s linear;
  will-change: transform;
  position: relative;
  z-index: 1;
}

.stats-item {
  display: inline-flex;
  align-items: center;
  margin-right: 2.5rem;
  min-width: max-content;
  flex-shrink: 0;
  padding: 0 0.5rem;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.stats-item:hover {
  transform: translateY(-2px);
}

.stats-item:hover .stats-icon-wrapper {
  transform: scale(1.1);
  background: rgba(255,255,255,0.25);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.stats-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  background: rgba(255,255,255,0.15);
  border-radius: 6px;
  margin-right: 10px;
  transition: all 0.2s ease;
}

.stats-icon-wrapper i {
  font-size: 16px;
  color: #ffffff;
}

/* Color variations */
.stats-icon-wrapper.warning-stat {
  background: rgba(251, 191, 36, 0.2);
}
.stats-icon-wrapper.warning-stat i {
  color: #fbbf24;
}

.stats-icon-wrapper.success-stat {
  background: rgba(34, 197, 94, 0.2);
}
.stats-icon-wrapper.success-stat i {
  color: #22c55e;
}

.stats-icon-wrapper.primary-stat {
  background: rgba(59, 130, 246, 0.2);
}
.stats-icon-wrapper.primary-stat i {
  color: #60a5fa;
}

.stats-icon-wrapper.money-stat {
  background: rgba(16, 185, 129, 0.2);
}
.stats-icon-wrapper.money-stat i {
  color: #10b981;
}

.stats-content {
  display: flex;
  flex-direction: column;
}

.stats-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #94a3b8;
  font-weight: 600;
  line-height: 1;
  margin-bottom: 3px;
}

.stats-value {
  font-size: 15px;
  font-weight: 700;
  color: #ffffff;
  line-height: 1;
}

.stats-divider {
  width: 1px;
  height: 28px;
  background: rgba(255,255,255,0.2);
  margin: 0 1rem;
}

/* Hover effect for stats banner */
.stats-banner-markets:hover .stats-scroll {
  animation-play-state: paused;
}

/* Navigation link styles */
.nav-link {
  transition: all 0.2s ease;
  border-radius: 6px;
  margin: 0 2px;
  padding: 8px 14px !important;
  font-weight: 500;
  font-size: 14px;
  color: #475569 !important;
  display: flex;
  align-items: center;
  border: 1px solid transparent;
}

.nav-link i {
  font-size: 16px;
  opacity: 0.7;
  color: #64748b;
}

.nav-link:hover {
  background-color: #f1f5f9;
  color: #1e293b !important;
}

.nav-link:hover i {
  color: #4f46e5;
  opacity: 1;
}

.nav-link.active {
  background-color: #4f46e5;
  color: #ffffff !important;
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(79, 70, 229, 0.3);
}

.nav-link.active i {
  opacity: 1;
  color: #ffffff;
}

/* Navbar toggler */
.navbar-toggler {
  color: #475569;
  border: 1px solid #e5e7eb;
}

/* Fade transition */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .stats-item {
    margin-right: 2rem;
  }

  .stats-banner-markets {
    height: 44px;
  }

  .stats-icon-wrapper {
    width: 28px;
    height: 28px;
  }

  .stats-label {
    font-size: 9px;
  }

  .stats-value {
    font-size: 13px;
  }

  .nav-link {
    font-size: 13px;
    padding: 6px 12px !important;
  }
}

@media (max-width: 480px) {
  .stats-item {
    margin-right: 1.5rem;
  }

  .stats-icon-wrapper {
    width: 26px;
    height: 26px;
    margin-right: 8px;
  }

  .stats-icon-wrapper i {
    font-size: 14px;
  }
}
</style>